/**
 * walkthrough_controller.js — the first-run walkthrough (Stimulus). Markup:
 * onboard_walkthrough in templates/macros/onboarding.html, one permanent
 * <dialog> per business in base.html. Rules: docs/ONBOARDING_DESIGN_RULES.md
 * W1 to W7, A1 to A5, M1 to M6.
 *
 * Every tap is on a real control the app has anyway; the run lights it,
 * explains it and, where the real control would send something, intercepts
 * it. On the dashboard: tap New Quote (start_quote), watch the real quote
 * form fill with a sample job and save it (fill_quote), then tap the saved
 * quote's real row. On that quote's page: tap Add Photo, and a sample photo
 * uploads through the real photo input (add_photo); see the PDF, then tap
 * Send and Email, and see the email instead of sending it (email_quote); tap
 * Send and SMS and see the text (text_quote); tap the link in the text to see
 * the client's page (client_view). The intercepted send leaves the sample
 * quoted, as a real one would. Back on the quote, a checkpoint offers
 * chapter 2 (Keep going) or ends the run (Later). Chapter 2 runs on the
 * quote, then the job and invoice the yes makes, walked to by the banners'
 * own View Job and View Invoice: Update Status, Accept (accept_quote); the
 * job's schedule control, its real modal set to tomorrow, its Schedule
 * (schedule_job); Start Job (start_job) and Mark Complete (finish_job),
 * real posts; the invoice's PDF, email and text, shown not sent
 * (send_invoice); its client's page, where the owner taps I've paid for the
 * client (invoice_client_view, acted out on the sample); Confirm Payment
 * (confirm_paid). Then find Chip (ask_chip). An offer card proposes it once
 * on a first-run dashboard; a line under the checklist and in the
 * navigation ring starts or resumes it.
 *
 * Where a run is has one record: the server's
 * (application/services/walkthrough_progress.py run_now), kept from the events
 * this controller reports and the sample's own records. This instance holds
 * only what is on screen (the card, the lit control, an open menu) for as
 * long as one document lasts. The dialog is data-turbo-permanent, so Stimulus
 * disconnects and reconnects it on every Turbo render and keeps this
 * instance: disconnect() tears down only when the element is really gone,
 * and connect() re-arms what the current phase listens to.
 *
 * The rule that keeps a run from stalling: every page that arrives mid-run
 * either carries the run on or reports its close, never neither. A page can
 * arrive whole at any moment: Turbo reloads whenever a page's assets differ
 * from the copy it is given (every deploy does that to the copies the
 * service worker saved), Back to a page loaded whole loads it whole, and the
 * back-forward cache restores a page as it was when it left. A page that
 * arrives whole asks the server where the run is (_arriveWhole); Back by a
 * Turbo restore within the run's pages carries on the run this instance
 * holds (_navigate). Carrying on (_continue) means running the run's step
 * from this page: here, or walking or visiting to its page (_enterStep,
 * _chapterStep). On a page that is not the run's (onRunPage) the close is
 * reported there. Never add a way onto a page that bypasses _continue.
 *
 * The dialog's open attribute is set only while a card shows, which holds
 * page-check swaps (page-check.js counts dialog[open] as busy). The body mark
 * (data-onboard-walkthrough) steps the Chip launcher aside while the card
 * owns the bottom of the screen; it is never set before load + 600ms, after
 * the native shell has read the page.
 *
 * Pure rules (what a tap or a visit means, when the screen is calm) are in
 * _walkthrough_run.js; the scripted fill is _walkthrough_fill.js; the PDF,
 * email and text previews are _walkthrough_preview.js; placement, motion and
 * timings are _onboard.js and the onboarding tokens. Events go through
 * reportProgress: walkthrough_offered, _started, _step_reached,
 * _step_completed {step, ms}, _closed {step, how} once per run, _finished,
 * and the demo's demo_name_prompt_shown / _tapped.
 */
import { Controller } from "@hotwired/stimulus";
import { animationsFinished, markDots, onboardTiming, placeCard, reveal, spotlightFrame, swapStep } from "./_onboard.js";
import { currentRun, reportProgress } from "./_onboarding_beacon.js";
import { loadTiptap } from "./_tiptap.js";
import { fillQuoteForm } from "./_walkthrough_fill.js";
import { drawEmail, drawPdf, drawText } from "./_walkthrough_preview.js";
import {
    absoluteUrl, CHAPTER_TWO, clientPage, detailPage, firstVisible, hasBox, markLabel, menuToggle, navigationVerdict,
    onRunPage, pathTowards, QUOTE_FORM_OPENERS, quoteControls, readMemo, SEND_CONTROLS, SEND_STATES, STEP_PAGE,
    screenIsCalm, shownOrInMenu, stepHere, stylesLoaded, tapVerdict, target, targetName, tomorrow, writeMemo,
} from "./_walkthrough_run.js";

const BODY_MARK = "data-onboard-walkthrough";
const QUOTE_FORM = "#quoteForm";
const FORMS = '[data-controller~="forms"]';
const CHAT = ".pchat--app";
const LAUNCHER = { web: `.pchat--app:not(.pchat--open) ${target("chip")}`, native: ".pchat--app:not(.pchat--open)" };
const PREVIEW = "[data-walkthrough-preview]";
/** The body mark and the offer wait this long after load (native shells read the page first). */
const SETTLE_MS = 600;
const OFFER_POLL_MS = 250;
const OFFER_GIVE_UP_MS = 8000;
/** The saved row is lit once the sheet has gone, or this long after the save at most. */
const SHEET_GONE_MS = 1000;
/** No saved marker this long after the save: the marker read failed; the line resumes later. */
const SAVED_GIVE_UP_MS = 4000;
/** The longest a quote page step waits for the page's stylesheets. */
const STYLES_GIVE_UP_MS = 2000;
const LAUNCHER_POLL_MS = 100;
const APPEAR_POLL_MS = 100;
/** A lit form's save streams its repaint in after it reports success; the next step waits this long. */
const STREAM_RENDER_MS = 150;
const LAUNCHER_GIVE_UP_MS = 2000;
/** A control a submit or a booking repaints in is waited for this long at most. */
const APPEAR_GIVE_UP_MS = 2000;
/** A control a cached copy of the page lacks is waited for (the page check's swap) this long at most. */
const PRESENT_GIVE_UP_MS = 5000;
/** A resize (a window dragged, a phone turned) settles this long before the lit control is looked for again. */
const RELAYOUT_MS = 150;
/** How long the picked day shows in the schedule modal before its save is lit. */
const PICK_SHOWN_MS = 1200;
/** The sample visit's start: tomorrow at this time (schedule_event_modal.html's options). */
const VISIT_TIME = "08:00";
/** Phases a visit, a reload or a tap can close (spec 3.5). */
const CLOSABLE = ["step", "fill", "armed"];
/**
 * Card states whose lit control is easy to miss: the detail banner's View Job
 * and View Invoice, a small link at the top of a page the save just repainted.
 * The spotlight pulses them (pointAt's emphasis).
 */
const EMPHASIZED = ["schedule_job", "send_invoice"];
/** Phases with a run under way, which a Turbo restore within the run's pages carries on. */
const RUNNING = ["step", "fill", "armed", "saving", "submitting", "detail", "client", "pending", "checkpoint"];
/** The step a lit Update Status item's save leads to (SUBMITS), for a restore while it posts. */
const SUBMIT_NEXT = { _accepted: "schedule_job", _started: "finish_job", _finished: "send_invoice", _confirmed: "ask_chip" };
/** The line step a page step (_pageStep) belongs to, where they differ. */
const LINE_STEP = { schedule_job_open: "schedule_job", send_invoice_pdf: "send_invoice" };
// A touch screen, as forms_controller.js and sheet_controller.js read it.
const TOUCH_QUERY = "(hover: none) or (pointer: coarse)";
/** The steps that run on the sample's own quote page, in order. */
const ON_QUOTE = ["add_photo", "email_quote", "text_quote", "client_view"];
/** Card states whose lit control is a real form an Update Status item submits, and what follows its save. */
const SUBMITS = {
    accept_quote_item: "_accepted", start_job_item: "_started",
    finish_job_item: "_finished", confirm_paid_item: "_confirmed",
};

/** The server's run (currentRun) in the shape _place gives. */
function placeOf(run) {
    return {
        step: run.step, pendingFor: run.pending ?? null, kind: null, quoteId: run.quote_id ?? null,
        quoteUrl: null, jobId: run.job_id ?? null, invoiceId: run.invoice_id ?? null, resumeIndex: null,
    };
}

export default class extends Controller {
    static targets = ["card", "viewport", "slot", "saved", "nudge", "zoom", "zoomBody"];
    static values = {
        steps: Array, places: Object, sample: Object, field: String, user: String, demo: Boolean,
        dashboard: String, samplePhoto: String, pdfjs: String,
    };
    static outlets = ["onboard-spotlight"];

    connect() {
        this._phase ??= "idle";
        this._timers ??= new Set();
        if (this._loadedAt === undefined) {
            this._noteLoad();
            this._arriveWhole();
        }
        this._rearm();
        this._hideFinishedLines();
        this._maybeOffer();
    }

    disconnect() {
        setTimeout(() => {
            if (!this.element.isConnected) this._teardown();
        }, 0);
    }

    get spotlight() {
        return this.hasOnboardSpotlightOutlet ? this.onboardSpotlightOutlet : null;
    }

    // MARK: Turbo and page events

    tap(event) {
        // The scripted fill clicks the real line-item controls itself.
        if (this._phase === "fill" && event.isTrusted === false) return;
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        const entry = target.closest("[data-walkthrough-entry]");
        const verdict = tapVerdict({
            phase: this._phase,
            inCard: this.cardTarget.contains(target) || (this.hasZoomTarget && this.zoomTarget.contains(target)),
            onTarget: this._onTarget(target),
            onEntry: Boolean(entry),
            warned: Boolean(this._warned),
        });
        if (verdict === "start") this._fromLine(event, entry);
        else if (verdict === "through") this._through(event);
        else if (verdict === "not_now" || verdict === "dismiss") this._leave();
        else if (verdict === "later") this.later();
        else if (verdict === "warn") this._warn(event);
        else if (verdict === "tap_outside") this._close("tap_outside");
    }

    escape() {
        if (this._zoomMode) this.unzoom();
        else if (this._phase === "offer" || this._phase === "end") this._leave();
        else if (this._phase === "checkpoint") this.later();
        else if (CLOSABLE.includes(this._phase)) this._close("close");
    }

    beforeVisit(event) {
        this._navigate(event.detail.url, this._ownUrl);
    }

    visited(event) {
        if (event.detail.action === "restore") this._navigate(event.detail.url, null, true);
    }

    /** The snapshot taken for Back must not keep the mark or the sample's values. */
    beforeCache() {
        document.body.removeAttribute(BODY_MARK);
        if (this._phase === "fill" || this._phase === "armed") document.querySelector(QUOTE_FORM)?.reset();
    }

    loaded() {
        if (this._ownUrl && absoluteUrl(location.href, location.href) === this._ownUrl) this._ownUrl = null;
        this._hideFinishedLines();
        if (this._followPlace) {
            const place = this._followPlace;
            this._followPlace = null;
            this._continue(place);
        } else if (this._phase === "client" && clientPage(location.pathname)) {
            this._onClientPage(clientPage(location.pathname));
        } else if (this._startOnDashboard !== undefined) {
            const index = this._startOnDashboard;
            this._startOnDashboard = undefined;
            if (this._onDashboard()) this._begin(index);
        } else if (this._phase === "detail" && this._detailStep) {
            // Once per visit: the page check's fresh copy loads again after the saved one.
            this._pageStep(this._detailStep);
        } else if (this._phase === "pending" && !document.body.hasAttribute("data-client-preview")) {
            this._afterPending();
        } else if (this._phase === "chat" && !document.querySelector(".pchat--open")) {
            this._showEnd();
        }
        this._maybeOffer();
    }

    submitting(event) {
        if (this._phase === "armed" && event.target === this._armedForm) {
            this._savedAt = undefined;
            this._phase = "saving";
        }
    }

    submitted(event) {
        if (this._phase === "submitting" && this._submit?.form === event.target) {
            const { done } = this._submit;
            this._submit = null;
            if (!event.detail.success) {
                this._leave();
                return;
            }
            // The save's stream repaints the page after this event; the next
            // step looks for its control on the repainted page.
            this._later(() => { if (this._phase === "submitting") this[done](); }, STREAM_RENDER_MS);
            return;
        }
        if (this._phase !== "saving" || event.target !== this._armedForm) return;
        if (!event.detail.success) {
            this._phase = "armed";
            this.spotlight?.pointAt(this._target);
            return;
        }
        this._complete("fill_quote");
        this._disarm();
        this.spotlight?.clear();
        this._savedAt = performance.now();
        this._later(() => { if (this._phase === "saving") this._leave(); }, SAVED_GIVE_UP_MS);
        this._maybeLightRow();
    }

    sheetShown() {
        this._sheetUp = true;
        this._onSheetShown?.();
        this._onSheetShown = null;
    }

    sheetHidden() {
        this._sheetUp = false;
        if (this._phase === "saving") this._maybeLightRow();
        else if (this._phase === "fill" || this._phase === "armed") this._close("tap_outside");
    }

    /** The marker the sample's save streams in names its quote; the run lights that quote's row. */
    savedTargetConnected(marker) {
        this._clientQuoteId = Number(marker.dataset.walkthroughQuoteId);
        if (this._phase === "saving") this._maybeLightRow();
    }

    /**
     * A menu (Send, Update Status) opened from its lit toggle: light the item
     * the step is about. Bootstrap's dropdown handler captures on document
     * ahead of this dialog's, so the menu opens before tap() has judged the
     * same click; the item is lit on the next task, once it has.
     */
    menuShown(event) {
        const menu = this._menu;
        if (this._phase !== "step" || !menu || this._state !== menu.state || !this._onTarget(event.target)) return;
        this._later(() => {
            if (this._onState(menu.state)) this._lightState(menu.item, menu.selector, "top", menu.relight);
        }, 0);
    }

    /**
     * The window resized. Many controls are one element on a wide screen and
     * another on a narrow one (Accept is a button, or an item in the Update
     * Status menu), so a step whose lit control no longer shows is lit again
     * where it shows now.
     */
    relayout() {
        clearTimeout(this._relayoutTimer);
        this._relayoutTimer = this._later(() => {
            const lit = this._lit;
            if (this._phase !== "step" || lit?.state !== this._state || hasBox(this._target)) return;
            lit.relight();
        }, RELAYOUT_MS);
    }

    clientViewSeen(event) {
        const { quoteId, invoiceId, via } = event.detail || {};
        if (this._phase !== "client" || via !== "walkthrough") return;
        if (this._sendKind === "quote" && Number(quoteId) === this._clientQuoteId) this._leftClientView();
        else if (this._sendKind === "invoice" && Number(invoiceId) === this._invoiceId) this._stepClientPaid();
    }

    /** The sample job's visit was booked in the real schedule modal: on to starting it. */
    async scheduled(event) {
        const { action, entityType, entityId } = event.detail || {};
        if (!["schedule_job_fill", "schedule_job_armed"].includes(this._state) || this._phase !== "step") return;
        if (action !== "create" || entityType !== "job" || Number(entityId) !== this._jobId) return;
        this._complete("schedule_job");
        this._phase = "submitting";
        this.spotlight?.clear();
        await this._modalGone(document.getElementById("scheduleEventModal"));
        if (this._phase === "submitting") this._stepStart();
    }

    /** The native More tab opens the menu with no click in the page; nothing may sit over it. */
    menuOpened() {
        if (CLOSABLE.includes(this._phase)) this._close("tap_outside");
        else if (this._phase === "offer" || this._phase === "end") this._leave();
    }

    /**
     * This page back from the back-forward cache. Its run is as it was when
     * the page left, which the pages after it may have moved on or closed:
     * the server's record decides, and none means the run is over.
     */
    pageshow(event) {
        if (!event.persisted) return;
        this._forget();
        this._arriveWhole();
    }

    // MARK: card actions

    start() {
        if (this._phase === "offer") this._begin(0);
    }

    notNow() {
        if (this._phase === "offer") this._leave();
    }

    close() {
        if (CLOSABLE.includes(this._phase)) this._close("close");
    }

    /** The PDF has been seen: on to the real Send button. */
    pdfSeen() {
        const states = SEND_STATES[this._sendKind];
        if (!states || !this._onState(states.pdf)) return;
        this._abort?.abort();
        this._lightSend(states.send, states.item, "email");
    }

    /** The email has been seen: on to the text, through the same Send button. */
    emailSeen() {
        const states = SEND_STATES[this._sendKind];
        if (!states || !this._onState(states.shown)) return;
        if (states.steps.text !== states.steps.email) {
            this._complete(states.steps.email);
            this._setStep(states.steps.text);
            this._reach(states.steps.text);
        }
        this._lightSend(states.text, states.textItem, "sms");
    }

    /**
     * The PDF or email in the card, full screen: a tap or click on it (or
     * its hint). The text has nothing to enlarge; its link is the step.
     */
    zoom() {
        if (this._zoomDraw) this._openZoom("full");
    }

    /** A tap on the dimmed edge around the document shuts the zoom, as its X does. */
    zoomEdge(event) {
        if (event.target === event.currentTarget) this.unzoom();
    }

    unzoom() {
        this._zoomMode = null;
        if (this.hasZoomTarget) this.zoomTarget.hidden = true;
    }

    /** The checkpoint's Keep going: chapter 2, from the sample's quote page. */
    keepGoing() {
        if (this._phase !== "checkpoint") return;
        this._chapterStep("accept_quote");
    }

    /** The checkpoint's Later: the run ends unfinished, and the line resumes at chapter 2. */
    later() {
        if (this._phase !== "checkpoint" || this._closed) return;
        this._closed = true;
        this._setStep("accept_quote");
        reportProgress("walkthrough_closed", { step: "accept_quote", how: "later" });
        this._leave();
    }

    next() {
        if (!this._onStep("ask_chip")) return;
        this._unwatchChat();
        this._finish();
        this._showEnd();
    }

    done() {
        if (this._phase === "end") this._leave();
    }

    nameBusiness() {
        reportProgress("demo_name_prompt_tapped");
    }

    // MARK: starting

    _fromLine(event, entry) {
        event.preventDefault();
        const lineIndex = Number(entry.dataset.walkthroughIndex) || 0;
        const index = Math.max(lineIndex, this._resumeIndex || 0);
        if (entry.dataset.walkthroughQuoteId) {
            this._clientQuoteId = Number(entry.dataset.walkthroughQuoteId);
            this._quoteUrl = entry.dataset.walkthroughQuoteUrl;
        }
        // Step 1 reports its own start; a later step tells the server the run is under way again.
        if (index > 0) reportProgress("walkthrough_resumed", { step: this.stepsValue[index] });
        this._begin(index);
    }

    _begin(index) {
        this._closed = false;
        const step = this.stepsValue[index];
        if (index <= this.stepsValue.indexOf("fill_quote") && !this._onDashboard()) {
            this._startOnDashboard = index;
            this._ownUrl = absoluteUrl(this.dashboardValue, location.href);
            window.Turbo?.visit(this.dashboardValue);
        } else if (step === "start_quote") {
            this._stepOne();
        } else if (step === "fill_quote") {
            window.showForm?.("quote");
            this._toFill();
        } else if (ON_QUOTE.includes(step) || STEP_PAGE[step]) {
            this._toQuotePage(step);
        } else {
            this._phase = "pending";
            this._setStep("ask_chip");
            this._stepChip();
        }
    }

    // MARK: step 1, the quote opener

    async _stepOne() {
        const target = firstVisible(document, QUOTE_FORM_OPENERS, { outside: ".dropdown-menu" });
        if (!target) {
            this._setStep("start_quote");
            this._missing(QUOTE_FORM_OPENERS);
            this._leave();
            return;
        }
        this._phase = "step";
        this._setStep("start_quote");
        this._state = "start_quote";
        this._target = target;
        // Reported before the card rises: a quick tap on the opener completes the step mid-rise.
        reportProgress("walkthrough_started");
        this._reach("start_quote");
        await this._showState("start_quote", target.textContent.trim(), this._cardSideFor(target) ?? "bottom", "step");
        await this._reveal(target);
        if (this._onStep("start_quote")) this._light(target);
    }

    /** The lit control was tapped: what it does depends on the card state it was lit for. */
    _through(event) {
        const state = this._state;
        const send = SEND_STATES[this._sendKind];
        if (state === "start_quote") {
            this._complete("start_quote");
            this._warmEditor();
            this._toFill();
        } else if (state === "quote_saved") {
            this._toQuotePage("add_photo", this._target.getAttribute("href"));
        } else if (state === "add_photo") {
            this._intercept(event);
            this._addSamplePhoto();
            this._complete("add_photo");
            this._stepPdf("quote");
        } else if (send && state === send.item) {
            this._intercept(event);
            this._showEmail();
        } else if (send && state === send.textItem) {
            this._intercept(event);
            this._showText();
        } else if (send && state === send.view) {
            this._toClientView();
        } else if (SUBMITS[state]) {
            this._awaitSubmit(SUBMITS[state]);
        } else if (state === "schedule_job") {
            this._toPage("schedule_job_open", this._target.getAttribute("href"), true);
        } else if (state === "schedule_job_open") {
            this._pickVisit();
        } else if (state === "send_invoice") {
            this._toPage("send_invoice_pdf", this._target.getAttribute("href"), true);
        } else if (state === "invoice_client_paid") {
            this._intercept(event);
            this._actOutPaid();
        }
        // A lit menu toggle opens its real menu (menuShown lights the item);
        // the lit schedule save posts the booking itself (scheduled).
    }

    /**
     * The real control would open a picker, a menu or a send: the run shows
     * its result instead. Bootstrap's own capture handler still sees the tap,
     * so the control's menu (Add Photo's, or the Send menu an item is in) is
     * closed after it.
     */
    _intercept(event) {
        event.preventDefault?.();
        event.stopPropagation?.();
        const toggle = this._target?.closest?.(".dropdown, .btn-group")?.querySelector('[data-bs-toggle="dropdown"]');
        if (toggle) setTimeout(() => window.bootstrap?.Dropdown?.getInstance(toggle)?.hide(), 0);
    }

    // MARK: step 2, the fill

    async _toFill() {
        this._phase = "fill";
        this._setStep("fill_quote");
        this._state = "fill_quote";
        this._target = null;
        this.spotlight?.clear();
        await this._showState("fill_quote", "", "top");
        if (!(await this._formShown()) || this._phase !== "fill") return;
        this._reach("fill_quote");
        this._abort = new AbortController();
        try {
            const button = await fillQuoteForm({
                form: document.querySelector(QUOTE_FORM),
                sample: this.sampleValue,
                timing: this._timing(),
                show: (targets, options) => this._showField(targets, options),
                note: (key) => this._swapTo(key, ""),
                signal: this._abort.signal,
            });
            if (this._phase === "fill") await this._arm(button);
        } catch (error) {
            if (error?.name !== "AbortError") this._close("close");
        }
    }

    /** Resolves true once the quote form is on screen: the sheet shown, or the panel slid in. */
    async _formShown() {
        await new Promise(requestAnimationFrame);
        const forms = document.querySelector(FORMS);
        if (!forms || !document.querySelector(QUOTE_FORM)) return false;
        if (!forms.classList.contains("modal")) {
            await animationsFinished(forms, { subtree: true });
            return true;
        }
        if (this._sheetUp) return true;
        await new Promise((resolve) => { this._onSheetShown = resolve; });
        return true;
    }

    /** A field the fill is at, with the quiet arrow; `point` draws it in afresh (a choice the card names). */
    async _showField(targets, { scrollTo, point = false } = {}) {
        const first = scrollTo || [targets].flat()[0];
        if (first) await this._reveal(first);
        this.spotlight?.pointAt(targets, { quiet: !point });
    }

    /** Only this submit of the form carries the sample field: Turbo builds its FormData from the form. */
    async _arm(button) {
        this._phase = "armed";
        this._target = button;
        this._armedForm = button.form;
        this._onFormData = (event) => event.formData.set(this.fieldValue, "1");
        this._armedForm.addEventListener("formdata", this._onFormData);
        await this._swapTo("fill_quote_armed", button.textContent.trim());
        await this._reveal(button);
        if (this._phase === "armed") this.spotlight?.pointAt(button);
    }

    /**
     * Scroll `target` clear of the card (_onboard.js reveal), waiting out the
     * spotlight's glide. A touch screen jumps: iOS spends the first tap during
     * a moving scroll on stopping it, so a glide would eat the tap on the
     * control it is bringing into view.
     */
    _reveal(target) {
        const timing = this._timing();
        const jump = timing.reduced || window.matchMedia?.(TOUCH_QUERY).matches;
        return reveal(target, this.cardTarget, { reduced: Boolean(jump), wait: timing.spotlight + 100 });
    }

    /** The description editor loads lazily; start it while the sheet slides in. */
    _warmEditor() {
        try {
            loadTiptap().catch(() => {});
        } catch {
            // A build without the editor chunk: the fill writes the plain textarea.
        }
    }

    _disarm() {
        this._armedForm?.removeEventListener("formdata", this._onFormData);
        this._armedForm = null;
    }

    /**
     * The form forgets the sample at once, and again on the next task after any
     * late write. That second reset is a bare timeout: _leave clears the run's.
     */
    _abandonForm(how) {
        this._abort?.abort();
        this._disarm();
        const form = document.querySelector(QUOTE_FORM);
        if (form) {
            form.reset();
            setTimeout(() => form.reset(), 0);
        }
        if (how === "close") window.hideAllForms?.();
    }

    // MARK: the saved quote's row

    _maybeLightRow() {
        if (this._phase !== "saving" || !this.hasSavedTarget) return;
        // Turbo can connect the streamed marker before it reports submit success.
        if (this._armedForm && this._savedAt === undefined) return;
        const waited = performance.now() - (this._savedAt ?? -Infinity);
        if (this._sheetUp && waited < SHEET_GONE_MS) {
            this._later(() => this._maybeLightRow(), SHEET_GONE_MS - waited);
            return;
        }
        this._lightRow();
    }

    /** The real row the save prepended, by its own link to the quote's page. */
    async _lightRow() {
        this._clearTimers();
        this._phase = "step";
        this._setStep("add_photo");
        await this._lightState("quote_saved", quoteControls(this._clientQuoteId).row);
    }

    // MARK: the steps on the sample's quote page

    /**
     * Open the sample's quote page, then run `step` there. `href` is the
     * tapped row's link, which Turbo follows itself; with none (a resume)
     * the run visits the page.
     */
    _toQuotePage(step, href = null) {
        const url = href || this._quoteUrl || (this._clientQuoteId ? `/quote/${this._clientQuoteId}` : null);
        if (!url) {
            this._begin(0);
            return;
        }
        this._toPage(step, url, Boolean(href));
    }

    /**
     * Open `url` as the run's own visit and run `step` there (loaded).
     * `tapped`: the person tapped a lit link to it, which Turbo follows
     * itself; else the run visits it, or on that page already runs the step.
     */
    _toPage(step, url, tapped = false) {
        this._detailStep = step;
        if (!tapped && new URL(url, location.href).pathname === location.pathname) {
            this._pageStep(step);
            return;
        }
        this._ownUrl = absoluteUrl(url, location.href);
        this._phase = "detail";
        if (tapped) setTimeout(() => { if (this._phase === "detail") this._leave("detail"); }, 0);
        else window.Turbo?.visit(url);
    }

    /**
     * The page's own stylesheets decide which copy of a control shows (the
     * Add Photo menu moves while a quote has no photos), so a step picks its
     * control only once they have loaded, STYLES_GIVE_UP_MS at most.
     */
    async _pageStep(step) {
        this._detailStep = null;
        const phase = this._phase;
        await stylesLoaded(document, STYLES_GIVE_UP_MS);
        if (this._phase !== phase) return;
        this._notePage();
        if (step === "add_photo") this._stepPhoto();
        else if (step === "email_quote") this._stepPdf("quote");
        else if (ON_QUOTE.includes(step)) this._stepText("quote", step);
        else if (step === "schedule_job_open") this._stepSchedule();
        else if (step === "send_invoice_pdf") this._stepPdf("invoice");
        else this._chapterStep(step);
    }

    /** The sample's quote, job or invoice this page is, by its path. */
    _notePage() {
        const page = detailPage(location.pathname);
        if (page?.kind === "quote") this._clientQuoteId = page.id;
        else if (page?.kind === "job") this._jobId = page.id;
        else if (page?.kind === "invoice") this._invoiceId = page.id;
    }

    /** Light the real Add Photo; with none on the page (no photos here) go on to the PDF. */
    async _stepPhoto() {
        const selector = quoteControls(this._clientQuoteId).add_photo;
        if (!firstVisible(document, selector)) {
            this._stepPdf("quote");
            return;
        }
        this._phase = "step";
        this._setStep("add_photo");
        this._reach("add_photo");
        await this._lightState("add_photo", selector);
    }

    /**
     * The sample gate photo, put into the quote's real photo input as a pick
     * would, so gallery_controller.js uploads it the usual way. Best effort: a
     * photo that fails to load leaves the run going without it.
     */
    async _addSamplePhoto() {
        const input = document.getElementById(`quote-${this._clientQuoteId}-photo-input`);
        if (!input) return;
        try {
            const blob = await (await fetch(this.samplePhotoValue, { credentials: "same-origin" })).blob();
            const files = new DataTransfer();
            files.items.add(new File([blob], "sample-gate.jpg", { type: blob.type || "image/jpeg" }));
            input.files = files.files;
            input.dispatchEvent(new Event("change", { bubbles: true }));
        } catch {
            // The quote keeps its other photos; the step still counts as seen.
        }
    }

    /**
     * The document's PDF in the card, then (pdfSeen) the real Send button.
     * `kind` is the quote (chapter 1) or the invoice (chapter 2), whose send
     * steps are the same (SEND_STATES).
     */
    async _stepPdf(kind) {
        const states = SEND_STATES[kind];
        this._sendKind = kind;
        this._phase = "step";
        this._setStep(states.steps.email);
        this._state = states.pdf;
        this._target = null;
        this.spotlight?.clear();
        if (kind === "quote") this._reach(states.steps.email);
        await this._showState(states.pdf, "", "bottom", "step");
        if (!this._onState(states.pdf)) return;
        this._abort = new AbortController();
        const url = `/${kind}/${this._docId()}/pdf`;
        drawPdf(this._preview(), url, this.pdfjsValue, { signal: this._abort.signal }).catch(() => {});
        this._zoomDraw = (box, signal) => drawPdf(box, url, this.pdfjsValue, { signal });
    }

    /** Light the real Send button for `state`; its `which` item (email, sms) is lit once it opens. */
    _lightSend(state, itemState, which) {
        return this._lightMenuItem(state, itemState, SEND_CONTROLS[which]);
    }

    /** The email the client would get, drawn from the server's composer, never sent. */
    async _showEmail() {
        const states = SEND_STATES[this._sendKind];
        this._state = states.shown;
        this._target = null;
        this.spotlight?.clear();
        this._actOutSend();
        await this._swapState(states.shown, "");
        const message = await this._json(`/${this._sendKind}/${this._docId()}/email-preview`);
        if (!message || !this._onState(states.shown)) return;
        drawEmail(this._preview(), message);
        this._zoomDraw = (box) => drawEmail(box, message);
    }

    /** Light Send again, for its SMS item (a resume at the client view starts here too). */
    _stepText(kind, step) {
        const states = SEND_STATES[kind];
        this._sendKind = kind;
        this._phase = "step";
        this._setStep(states.steps.text);
        if (step === states.steps.text && kind === "quote") this._reach(states.steps.text);
        this._lightSend(states.text, states.textItem, "sms");
    }

    /** The text the client would get, in a bubble whose link opens the client's page. */
    async _showText() {
        const kind = this._sendKind;
        const states = SEND_STATES[kind];
        this._complete(states.steps.text);
        this._setStep(states.steps.view);
        this._state = states.view;
        this._target = null;
        this.spotlight?.clear();
        this._actOutSend();
        await this._swapState(states.view, "");
        const message = await this._json(`/${kind}/${this._docId()}/sms-preview`);
        if (!message?.body || !this._onState(states.view)) {
            if (this._onState(states.view)) this._leave();
            return;
        }
        const ownerUrl = `/${kind}/${this._docId()}/client-preview?via=walkthrough`;
        const { link } = drawText(this._preview(), message, ownerUrl);
        link.dataset.clientPreviewLink = String(this._docId());
        this._target = link;
        this.spotlight?.pointAt(link);
        this._reach(states.steps.view);
    }

    /**
     * The sample quote goes out as a real send would leave it (quoted), so the
     * client's view and chapter 2's Accept see it sent; nothing is sent. The
     * device's saved copy of its page is brought up to date for the way back.
     */
    async _actOutSend() {
        if (this._sendKind !== "quote") return;
        const page = `/quote/${this._clientQuoteId}`;
        if (await this._post(`${page}/sample-sent`)) window.chippiePageCheck?.refreshCopy?.(page);
    }

    /** The sample quote's id in chapter 1, the sample invoice's in chapter 2. */
    _docId() {
        return this._sendKind === "invoice" ? this._invoiceId : this._clientQuoteId;
    }

    /** The target's own click follows the link; leave after Turbo has captured it. */
    _toClientView() {
        const link = this._target;
        this._ownUrl = absoluteUrl(link.getAttribute("href"), location.href);
        this._phase = "client";
        setTimeout(() => { if (this._phase === "client") this._leave("client"); }, 0);
    }

    /**
     * On the client's view of the sample, by whatever way it came: the
     * quote's counts as seen; the invoice's lights the client's I've paid.
     * The view's own seen event (clientViewSeen) is not waited for: it needs
     * the server's answer and never comes for a repaint of a saved copy.
     */
    _onClientPage(client) {
        if (client.kind === "quote" && client.id === this._clientQuoteId) {
            this._sendKind = "quote";
            this._leftClientView();
        } else if (client.kind === "invoice" && client.id === this._invoiceId) {
            this._sendKind = "invoice";
            this._stepClientPaid();
        } else {
            this._enterStep(this._stepName());
        }
    }

    // MARK: arriving: every page carries the run on or reports its close

    /** The run this instance holds, to carry on from the page a Turbo restore lands on; null with none under way. */
    _place() {
        const step = this._placeStep();
        if (!step) return null;
        const pending = this._phase === "checkpoint" ? "checkpoint" : this._phase === "pending" ? this._pendingFor : null;
        return {
            step, pendingFor: pending ?? null,
            kind: this._sendKind ?? null, quoteId: this._clientQuoteId ?? null, quoteUrl: this._quoteUrl ?? null,
            jobId: this._jobId ?? null, invoiceId: this._invoiceId ?? null, resumeIndex: this._resumeIndex ?? null,
        };
    }

    /**
     * The step the run carries on with on the next page. A save in flight
     * goes on with the step its save leads to; a sample quote not yet saved
     * cannot be carried on (null): typing it again would make a second.
     */
    _placeStep() {
        const phase = this._phase;
        if (this._closed || !RUNNING.includes(phase)) return null;
        if (phase === "detail") return LINE_STEP[this._detailStep] ?? this._detailStep ?? this._stepName();
        if (phase === "submitting") return SUBMIT_NEXT[this._submit?.done] ?? this._stepName();
        if (phase === "saving") return this._clientQuoteId ? "add_photo" : null;
        if (phase === "checkpoint") return "accept_quote";
        return this._stepName();
    }

    /**
     * A whole page arrived (a first load, or one back from the back-forward
     * cache): the run the server has under way carries on here, or its close
     * is reported when this page is not one of the run's. The offer waits for
     * the answer, so it never rises over a run being taken up.
     */
    async _arriveWhole() {
        this._arriving = true;
        const run = await currentRun();
        this._arriving = false;
        if (!run || this._phase !== "idle") {
            this._maybeOffer();
            return;
        }
        const place = placeOf(run);
        this._restore(place);
        if (this._isRunPage(location.pathname, place.step)) {
            this._continue(place);
            return;
        }
        this._closed = true;
        reportProgress("walkthrough_closed", { step: place.step, how: "navigation" });
    }

    _restore(place) {
        this._closed = false;
        this._sendKind = place.kind ?? undefined;
        this._clientQuoteId = place.quoteId ?? undefined;
        this._quoteUrl = place.quoteUrl ?? undefined;
        this._jobId = place.jobId ?? undefined;
        this._invoiceId = place.invoiceId ?? undefined;
        this._resumeIndex = place.resumeIndex ?? undefined;
        this._pendingFor = place.pendingFor;
        this._setStep(place.step);
    }

    /**
     * Whether the run can carry `step` on from `pathname`: the sample's
     * pages, and the dashboard for the steps that start there (chapter 1's
     * first two) or need no page (Chip).
     */
    _isRunPage(pathname, step) {
        const fromDashboard = this.stepsValue.indexOf(step) <= this.stepsValue.indexOf("fill_quote") || step === "ask_chip";
        return onRunPage(pathname, {
            dashboard: fromDashboard ? new URL(this.dashboardValue, location.href).pathname : null,
            quoteId: this._clientQuoteId ?? null, jobId: this._jobId ?? null, invoiceId: this._invoiceId ?? null,
        });
    }

    /**
     * Carry `place` on from this page: on the client's view of the sample,
     * that view's step; back from it, the checkpoint or Chip; else the step
     * the place names, here or by walking or visiting to its page.
     */
    _continue(place) {
        this._restore(place);
        const client = clientPage(location.pathname);
        if (place.pendingFor) {
            this._phase = "pending";
            if (!client) this._afterPending();
        } else if (client) {
            this._onClientPage(client);
        } else {
            this._enterStep(place.step);
        }
    }

    /** Run line step `step` from this page, whatever brought the run here. */
    _enterStep(step) {
        const page = detailPage(location.pathname);
        const here = page && (ON_QUOTE.includes(step) ? page.kind === "quote" : Boolean(STEP_PAGE[step]));
        if (!here) {
            this._begin(Math.max(0, this.stepsValue.indexOf(step)));
            return;
        }
        this._phase = "detail";
        this._pageStep(step);
    }

    /** Drop this page's run without a word: the place another page kept (or none) decides. */
    _forget() {
        this._startOnDashboard = undefined;
        this._detailStep = null;
        this._followPlace = null;
        this._closed = false;
        this._leave();
    }

    _leftClientView() {
        this._complete("client_view");
        this._phase = "pending";
        this._pendingFor = "checkpoint";
        this._setStep("accept_quote");
    }

    /**
     * Back on a page after the client view: the checkpoint, on the sample's
     * quote by its Accept (visited first from any other page); after the
     * payment: Chip.
     */
    _afterPending() {
        if (this._pendingFor !== "checkpoint") {
            this._stepChip();
            return;
        }
        const url = this._quoteUrl || (this._clientQuoteId ? `/quote/${this._clientQuoteId}` : null);
        if (detailPage(location.pathname)?.kind !== "quote" && url) {
            this._ownUrl = absoluteUrl(url, location.href);
            window.Turbo?.visit(url);
            return;
        }
        this._whenPresent(shownOrInMenu(CHAPTER_TWO.accept), () => this._showCheckpoint());
    }

    // MARK: the checkpoint between the chapters

    async _showCheckpoint() {
        this._pendingFor = null;
        this._phase = "checkpoint";
        this._state = "checkpoint";
        this._target = null;
        this.spotlight?.clear();
        await this._showState("checkpoint", "", "bottom", "offer");
        if (this._phase === "checkpoint") this.spotlight?.veil();
    }

    // MARK: chapter 2, from yes to paid

    /**
     * Run chapter 2 `step` here, or walk the banners' links (View Job, View
     * Invoice) from the sample's quote to the page it starts on (STEP_PAGE).
     * Every page on that walk is the sample's: it starts at its quote. A
     * resume paints the device's saved copy of that quote, which can predate
     * the yes and so lack its View Job until the page check swaps the fresh
     * copy in; the walk waits for it (_whenPresent).
     */
    _chapterStep(step) {
        const page = detailPage(location.pathname);
        const need = STEP_PAGE[step];
        if (!need) {
            this._leave();
            return;
        }
        // Off the sample's pages, or behind where the step starts: from its quote.
        if (!page) {
            this._toQuotePage(step);
            return;
        }
        this._notePage();
        const onward = stepHere(step, page.kind);
        if (onward === "schedule_job_open") {
            this._stepSchedule();
            return;
        }
        if (onward === "send_invoice_pdf") {
            this._stepPdf("invoice");
            return;
        }
        if (page.kind !== need) {
            const link = pathTowards(page.kind, need);
            if (!link) {
                this._toQuotePage(step);
                return;
            }
            this._whenPresent(link, () => this._toPage(step, firstVisible(document, link).getAttribute("href")));
            return;
        }
        if (step === "accept_quote") this._stepAccept();
        else if (step === "schedule_job") this._stepViewJob();
        else if (step === "start_job") this._stepStart();
        else if (step === "finish_job") this._stepFinish();
        else if (step === "send_invoice") this._stepViewInvoice();
        else if (step === "invoice_client_view") this._stepText("invoice", step);
        else this._whenPresent(shownOrInMenu(CHAPTER_TWO.confirm), () => this._stepConfirm());
    }

    _stepAccept() {
        this._phase = "step";
        this._setStep("accept_quote");
        this._reach("accept_quote");
        this._lightMenuItem("accept_quote", "accept_quote_item", CHAPTER_TWO.accept);
    }

    _accepted() {
        this._complete("accept_quote");
        this._stepViewJob();
    }

    /** The job the yes made, by the banner's own View Job. */
    async _stepViewJob() {
        this._phase = "step";
        this._setStep("schedule_job");
        this._reach("schedule_job");
        await this._lightWhenThere("schedule_job", CHAPTER_TWO.viewJob);
    }

    _stepSchedule() {
        this._phase = "step";
        this._setStep("schedule_job");
        this._lightState("schedule_job_open", CHAPTER_TWO.schedule);
    }

    /**
     * The real schedule modal opened from the lit control: pick tomorrow at
     * VISIT_TIME in it as a tap on its calendar would, then light its save.
     */
    async _pickVisit() {
        const modal = document.getElementById("scheduleEventModal");
        this._state = "schedule_job_fill";
        this._target = null;
        this.spotlight?.clear();
        await this._showState("schedule_job_fill", "", "top", "step");
        if (!modal || !(await this._modalUp(modal)) || !this._onState("schedule_job_fill")) return;
        modal.dispatchEvent(new CustomEvent("schedule-modal:pick", { detail: { date: tomorrow(new Date()), time: VISIT_TIME } }));
        await new Promise((resolve) => this._later(resolve, PICK_SHOWN_MS));
        if (!this._onState("schedule_job_fill")) return;
        const save = await this._appear(`${CHAPTER_TWO.saveVisit}:not([disabled])`);
        if (save && this._onState("schedule_job_fill")) this._lightState("schedule_job_armed", CHAPTER_TWO.saveVisit);
        else if (this._onState("schedule_job_fill")) this._leave();
    }

    _stepStart() {
        this._phase = "step";
        this._setStep("start_job");
        this._reach("start_job");
        this._lightMenuItem("start_job", "start_job_item", CHAPTER_TWO.start);
    }

    _started() {
        this._complete("start_job");
        this._stepFinish();
    }

    _stepFinish() {
        this._phase = "step";
        this._setStep("finish_job");
        this._reach("finish_job");
        this._lightMenuItem("finish_job", "finish_job_item", CHAPTER_TWO.finish);
    }

    _finished() {
        this._complete("finish_job");
        this._stepViewInvoice();
    }

    /** The invoice finishing the job readied, by the banner's own View Invoice. */
    async _stepViewInvoice() {
        this._phase = "step";
        this._setStep("send_invoice");
        this._reach("send_invoice");
        await this._lightWhenThere("send_invoice", CHAPTER_TWO.viewInvoice);
    }

    /** On the owner's view of the invoice: light the client's I've paid. */
    _stepClientPaid() {
        this._phase = "step";
        this._setStep("invoice_client_view");
        this._lightState("invoice_client_paid", CHAPTER_TWO.paid);
    }

    /**
     * The client's I've paid, acted out on the sample (nobody is told), then
     * back to the real invoice page with the card down, whose saved copy is
     * brought up to date first; the Confirm waits there for the page check
     * if it still lags.
     */
    async _actOutPaid() {
        this._phase = "submitting";
        this._target = null;
        this.spotlight?.clear();
        const page = `/invoice/${this._invoiceId}`;
        if (!(await this._post(`${page}/sample-paid`))) {
            this._leave();
            return;
        }
        this._complete("invoice_client_view");
        window.chippiePageCheck?.refreshCopy?.(page);
        // No card on the way: the page check holds its swap while one is up.
        await this._leave("detail");
        this._toPage("confirm_paid", page);
    }

    _stepConfirm() {
        this._phase = "step";
        this._setStep("confirm_paid");
        this._reach("confirm_paid");
        this._lightMenuItem("confirm_paid", "confirm_paid_item", CHAPTER_TWO.confirm);
    }

    /** Paid: the card goes down first, since the body mark steps the Chip launcher aside. */
    async _confirmed() {
        this._complete("confirm_paid");
        this._pendingFor = "chip";
        this._setStep("ask_chip");
        await this._leave("pending");
        if (this._phase === "pending") this._stepChip();
    }

    /**
     * A lit Update Status (or Send) item is a real form: its post goes
     * through, and `done` (a method name) runs once it has saved. A failed
     * save ends the run; the line resumes it.
     */
    _awaitSubmit(done) {
        const form = this._target?.closest?.("form");
        if (!form) {
            this._leave();
            return;
        }
        this._submit = { form, done };
        this._phase = "submitting";
        this.spotlight?.clear();
    }

    // MARK: the last step, Chip

    async _stepChip() {
        const target = await this._launcher();
        if (!target || this._phase !== "pending") return;
        this._phase = "step";
        this._setStep("ask_chip");
        this._state = "ask_chip";
        this._target = target;
        await this._showState("ask_chip", "", "top");
        if (!this._onStep("ask_chip")) return;
        this._light(target, { shape: "round" });
        this._watchChat();
        this._reach("ask_chip");
    }

    /** The Chip launcher once it has a box, polled briefly; never under a sheet or the keyboard. */
    async _launcher() {
        const native = document.documentElement.hasAttribute("data-hotwire-native");
        const selector = native ? LAUNCHER.native : LAUNCHER.web;
        for (let waited = 0; waited <= LAUNCHER_GIVE_UP_MS; waited += LAUNCHER_POLL_MS) {
            if (document.body.classList.contains("modal-open") || document.body.hasAttribute("data-keyboard-open")) return null;
            const target = firstVisible(document, selector);
            if (target) return target;
            await new Promise((resolve) => this._later(resolve, LAUNCHER_POLL_MS));
        }
        return null;
    }

    _watchChat() {
        const chat = document.querySelector(CHAT);
        if (!chat) return;
        this._chatObserver?.disconnect();
        this._chatObserver = new MutationObserver(() => this._chatChanged(chat));
        this._chatObserver.observe(chat, { attributes: true, attributeFilter: ["class"] });
    }

    _unwatchChat() {
        this._chatObserver?.disconnect();
        this._chatObserver = null;
    }

    _chatChanged(chat) {
        const open = chat.classList.contains("pchat--open");
        if (open && this._onStep("ask_chip")) {
            document.dispatchEvent(new CustomEvent("chippie:open-chip"));
            this._finish();
            this._leave("chat");
            this._watchChat();
        } else if (!open && this._phase === "chat") {
            this._unwatchChat();
            this._showEnd();
        }
    }

    _finish() {
        this._complete("ask_chip");
        reportProgress("walkthrough_finished");
        writeMemo(this._storage(), this.userValue, "finished");
    }

    // MARK: the end and the offer

    async _showEnd() {
        this._phase = "end";
        this._index = this.stepsValue.length;
        this._state = null;
        this._target = null;
        this.spotlight?.clear();
        await this._showState(this.demoValue ? "end_demo" : "end", "", "bottom", "end");
        if (this.demoValue) reportProgress("demo_name_prompt_shown");
        this._hideFinishedLines();
    }

    _maybeOffer() {
        if (this._phase !== "idle" || this._offering || this._arriving || !this._offerFlag()) return;
        const memo = readMemo(this._storage(), this.userValue);
        if (memo === "offered" || memo === "finished") return;
        this._offering = true;
        const start = performance.now();
        const poll = () => {
            if (this._phase !== "idle" || !this._offerFlag() || performance.now() - start > OFFER_GIVE_UP_MS) {
                this._offering = false;
            } else if (this._settled() && screenIsCalm(document, window, this.element)) {
                this._offering = false;
                this._offer();
            } else {
                this._later(poll, OFFER_POLL_MS);
            }
        };
        poll();
    }

    async _offer() {
        this._phase = "offer";
        this._index = 0;
        await this._showState("offer", "", "bottom", "offer");
        if (this._phase === "offer") this.spotlight?.veil();
        reportProgress("walkthrough_offered");
        writeMemo(this._storage(), this.userValue, "offered");
    }

    _offerFlag() {
        return Boolean(document.querySelector("template[data-walkthrough-offer]"));
    }

    // MARK: leaving

    /**
     * A Turbo visit to `url`. Back or Forward (`restore`) onto one of the
     * run's pages carries the run on there, as a whole page arriving would;
     * a visit anywhere else mid-step closes it (W2c).
     */
    _navigate(url, ownUrl, restore = false) {
        const verdict = navigationVerdict({
            phase: this._phase,
            swapping: Boolean(window.chippiePageCheck?.isSwapping()),
            url,
            ownUrl,
        });
        // Leaving the quote's client view counts it as seen (advance); the invoice's has a step there still.
        const followed = verdict === "close" ? restore : verdict === "advance" && this._sendKind === "invoice";
        if (followed
            && this._isRunPage(new URL(url, location.href).pathname, this._placeStep())) {
            this._follow();
        } else if (verdict === "close") {
            this._close("navigation");
        } else if (verdict === "dismiss") {
            this._leave();
        } else if (verdict === "advance") {
            this._leftClientView();
        }
    }

    /** The card goes down for the visit; the page it lands on carries the run on (loaded). */
    _follow() {
        const place = this._place();
        if (!place) return;
        this._leave("detail");
        this._detailStep = null;
        this._followPlace = place;
    }

    /**
     * A first tap outside: held, so nothing behind the card acts on it, and
     * the card nudges with how to carry on (the control its words name, or
     * its own action) and that a second tap outside leaves. A new card state
     * takes the warning back (_clearNudge).
     */
    _warn(event) {
        event.preventDefault?.();
        event.stopImmediatePropagation?.();
        this._warned = true;
        if (!this.hasNudgeTarget) return;
        const viewport = this.viewportTarget;
        const label = (viewport.querySelector("[data-walkthrough-label]")?.textContent
            || viewport.querySelector(".onboard-action")?.textContent || "").trim();
        const nudge = this.nudgeTarget;
        const labelled = nudge.querySelector('[data-nudge="label"]');
        labelled.querySelector("[data-walkthrough-label]").textContent = label;
        labelled.hidden = !label;
        nudge.querySelector('[data-nudge="plain"]').hidden = Boolean(label);
        nudge.hidden = false;
        // Off and on again, so a second warning on one card shakes it too.
        delete this.cardTarget.dataset.nudged;
        void this.cardTarget.offsetWidth;
        this.cardTarget.dataset.nudged = "";
    }

    _clearNudge() {
        this._warned = false;
        if (this.hasNudgeTarget) this.nudgeTarget.hidden = true;
        delete this.cardTarget.dataset.nudged;
    }

    /** Once per run: report how it closed, drop the sample from the form, then leave. */
    _close(how) {
        if (this._closed) return;
        this._closed = true;
        reportProgress("walkthrough_closed", { step: this._stepName(), how });
        if (this._phase === "fill" || this._phase === "armed") this._abandonForm(how);
        this._leave();
    }

    /** Card and spotlight out, dialog closed, body mark off; `phase` is where the run goes next. */
    async _leave(phase = "idle") {
        this._phase = phase;
        this._state = null;
        this._target = null;
        this._lit = null;
        this._menu = null;
        this._submit = null;
        this._abort?.abort();
        this._clearTimers();
        this._unwatchChat();
        this._disarm();
        this._clearNudge();
        this._dropZoom();
        this.spotlight?.clear();
        document.body.removeAttribute(BODY_MARK);
        await this._cardOut();
        // A new card may have started while this one left; only a cardless phase closes the dialog.
        if (["idle", "detail", "client", "pending", "chat"].includes(this._phase)) this.element.removeAttribute("open");
    }

    _teardown() {
        this._abort?.abort();
        this._clearTimers();
        this._unwatchChat();
        this._disarm();
        if (this._loadListener) window.removeEventListener("load", this._loadListener);
        document.body.removeAttribute(BODY_MARK);
        this._phase = "idle";
    }

    // MARK: the card

    /**
     * Swap the card to state `key` (its <template>), docking it to `placement`;
     * opens the dialog. `mark` steps the Chip launcher aside before the card
     * rises, so the launcher never shows under it.
     */
    async _showState(key, label, placement, mark = null) {
        const card = this.cardTarget;
        this._clearNudge();
        this._dropZoom();
        this._dock(placement);
        this.element.setAttribute("open", "");
        if (mark) this._mark(mark);
        if (!card.hidden && card.dataset.state !== "out") {
            await this._swapTo(key, label);
            return;
        }
        // The card shows empty and its words arrive a frame later, so the
        // polite live region announces them (one that appears full is silent).
        this._fillHead(key);
        this.viewportTarget.replaceChildren();
        card.hidden = false;
        card.dataset.state = "in";
        await new Promise(requestAnimationFrame);
        this.viewportTarget.replaceChildren(this._stateNode(key, label));
        if (key === "offer" || key.startsWith("end")) {
            this.viewportTarget.querySelector(".onboard-action")?.focus({ preventScroll: true });
        }
        await animationsFinished(card);
        if (card.dataset.state === "in") delete card.dataset.state;
    }

    /**
     * The card to state `key` with the control `selector` names lit, the card
     * docked away from it. The selector is kept: a save's repaint can replace
     * the control while it is lit (_onTarget finds it again). `relight` lights
     * the step again after a resize hides the control (relayout); a menu item
     * passes its own, since at the new width it may be in a menu or out of one.
     */
    async _lightState(key, selector, placement = "top", relight = null) {
        const target = firstVisible(document, selector);
        this._state = key;
        this._target = target;
        this._lit = { state: key, selector, relight: relight ?? (() => this._lightState(key, selector, placement)) };
        if (!target) {
            // Never end silently: say which mark is missing, and keep the card up
            // naming the control, unlit; a tap on it still counts (_onTarget).
            this._missing(selector);
            this.spotlight?.clear();
            await this._showState(key, markLabel(targetName(selector)), placement, "step");
            return;
        }
        await this._showState(key, target.textContent.trim(), this._cardSideFor(target) ?? placement, "step");
        await this._reveal(target);
        if (this._onState(key)) {
            this._light(target, { find: () => firstVisible(document, selector), emphasis: EMPHASIZED.includes(key) });
        }
    }

    /** The card to state `key`, as a step forward or as the card rising. */
    _swapState(key, label) {
        return this._showState(key, label, "bottom", "step");
    }

    _swapTo(key, label) {
        this._clearNudge();
        this._dropZoom();
        this._fillHead(key);
        return swapStep(this.viewportTarget, this._stateNode(key, label), "forward");
    }

    _stateNode(key, label) {
        const template = this.element.querySelector(`template[data-walkthrough-state="${key}"]`);
        const node = template.content.firstElementChild.cloneNode(true);
        node.querySelectorAll("[data-walkthrough-label]").forEach((slot) => { slot.textContent = label; });
        return node;
    }

    /** The preview box of the card state on show (data-walkthrough-preview). */
    _preview() {
        return this.viewportTarget.querySelector(PREVIEW);
    }

    /**
     * Show the zoom as `mode` ('full': full screen with its X),
     * drawing the card's document into it at its own size the first time.
     * The box is shown before the draw: a PDF draws at the width it measures.
     */
    _openZoom(mode) {
        if (!this.hasZoomTarget) return;
        this._zoomMode = mode;
        this.zoomTarget.dataset.mode = mode;
        this.zoomTarget.hidden = false;
        if (this._zoomDrawn === this._zoomDraw) return;
        this._zoomDrawn = this._zoomDraw;
        this._zoomAbort?.abort();
        this._zoomAbort = new AbortController();
        Promise.resolve(this._zoomDraw(this.zoomBodyTarget, this._zoomAbort.signal)).catch(() => {});
    }

    /** A new card state, or the run leaving: nothing left to enlarge. */
    _dropZoom() {
        this.unzoom();
        this._zoomDraw = null;
        this._zoomDrawn = null;
        this._zoomAbort?.abort();
        if (this.hasZoomBodyTarget) this.zoomBodyTarget.replaceChildren();
    }

    /** The offer, the checkpoint and the end carry no step dots and no close (W4, W5). */
    _fillHead(key) {
        const bare = key === "offer" || key === "checkpoint" || key.startsWith("end");
        const dots = this.cardTarget.querySelector(".onboard-dots");
        dots?.toggleAttribute("hidden", bare);
        this.cardTarget.querySelector(".onboard-card__close")?.toggleAttribute("hidden", bare);
        if (!bare) this._markPlace(dots);
    }

    /** One dot per step of the current step's chapter (placesValue), the current one marked. */
    _markPlace(dots) {
        if (!dots) return;
        const [number, total] = this.placesValue[this._stepName()] || [this._index + 1, dots.children.length];
        while (dots.children.length < total) dots.append(document.createElement("li"));
        while (dots.children.length > total) dots.lastElementChild.remove();
        markDots(dots, number - 1);
    }

    _dock(placement) {
        const card = this.cardTarget;
        if (card.hidden || card.dataset.state === "out") card.dataset.placement = placement;
        else placeCard(card, placement);
    }

    async _cardOut() {
        const card = this.cardTarget;
        if (card.hidden) return;
        card.dataset.state = "out";
        await animationsFinished(card);
        if (card.dataset.state !== "out") return;
        card.hidden = true;
        delete card.dataset.state;
        this.viewportTarget.replaceChildren();
    }

    /**
     * The side the card docks to once `target` is lit (spotlightFrame's rule),
     * so a step's card arrives there rather than on a default side and then
     * crossing the screen. Null without a spotlight to measure against.
     */
    _cardSideFor(target) {
        const layer = this.spotlight?.element?.getBoundingClientRect();
        return layer ? spotlightFrame(target.getBoundingClientRect(), layer, 0).card : null;
    }

    /** Light `target` with the arrow, and dock the card on the side away from it. */
    _light(target, options = {}) {
        const frame = this.spotlight?.pointAt(target, options);
        if (frame) placeCard(this.cardTarget, frame.card);
    }

    // MARK: helpers

    /** A preview's JSON, or null when it could not be read. */
    async _json(url) {
        try {
            const response = await fetch(url, { credentials: "same-origin", headers: { Accept: "application/json" } });
            return response.ok ? await response.json() : null;
        } catch {
            return null;
        }
    }

    /** POST `url` as the page's own forms do (CSRF header); true when it saved. */
    async _post(url) {
        try {
            const token = document.querySelector("meta[name=csrf-token]")?.getAttribute("content") || "";
            const response = await fetch(url, {
                method: "POST", credentials: "same-origin",
                headers: { Accept: "application/json", "X-CSRFToken": token },
            });
            return response.ok;
        } catch {
            return false;
        }
    }

    /**
     * The first element matching `selector` that has a box, polled until it
     * does or `giveUpMs` passes (null). A Turbo Stream repaints a control a
     * frame or more after its save reports success.
     */
    async _appear(selector, giveUpMs = APPEAR_GIVE_UP_MS) {
        for (let waited = 0; waited <= giveUpMs; waited += APPEAR_POLL_MS) {
            const found = firstVisible(document, selector);
            if (found) return found;
            await new Promise((resolve) => this._later(resolve, APPEAR_POLL_MS));
        }
        return null;
    }

    /**
     * Run `then` once `selector` is on the page. A page the device served
     * from its saved copy may lack it until the page check swaps the fresh
     * one in, which it does only while no card is up; none is, here.
     */
    async _whenPresent(selector, then) {
        const phase = this._phase;
        const found = await this._appear(selector, PRESENT_GIVE_UP_MS);
        if (this._phase !== phase) return;
        if (found) {
            then();
            return;
        }
        this._missing(selector);
        this._leave();
    }

    /** Report the walkthrough mark `selector` looks for as not on this page (WALKTHROUGH_TARGETS). */
    _missing(selector) {
        const name = targetName(selector);
        if (name) reportProgress("walkthrough_target_missing", { step: this._stepName(), target: name });
    }

    /** `_lightState`, once the control a save repaints in has appeared. */
    async _lightWhenThere(state, selector) {
        const phase = this._phase;
        await this._appear(selector);
        if (this._phase === phase) await this._lightState(state, selector);
    }

    /**
     * Light the control `item` names (a Send or Update Status item) as
     * `itemState` when it shows on its own, else its menu's toggle as `state`;
     * the item is lit once the menu opens (menuShown).
     */
    async _lightMenuItem(state, itemState, item) {
        const phase = this._phase;
        await this._appear(shownOrInMenu(item));
        if (this._phase !== phase) return;
        const relight = () => this._lightMenuItem(state, itemState, item);
        if (firstVisible(document, item)) {
            this._menu = null;
            await this._lightState(itemState, item, "top", relight);
            return;
        }
        this._menu = { state, item: itemState, selector: item, relight };
        await this._lightState(state, menuToggle(item), "top", relight);
    }

    /** True once Bootstrap modal `modal` is shown, false if it is not within APPEAR_GIVE_UP_MS. */
    _modalUp(modal) {
        return this._modalEvent(modal, "shown.bs.modal", modal.classList.contains("show"));
    }

    /** Resolves once modal `modal` has hidden (or APPEAR_GIVE_UP_MS passes). */
    _modalGone(modal) {
        return modal ? this._modalEvent(modal, "hidden.bs.modal", !modal.classList.contains("show")) : Promise.resolve(true);
    }

    _modalEvent(modal, type, already) {
        if (already) return Promise.resolve(true);
        return new Promise((resolve) => {
            const timer = this._later(() => {
                modal.removeEventListener(type, onEvent);
                resolve(false);
            }, APPEAR_GIVE_UP_MS);
            const onEvent = () => {
                clearTimeout(timer);
                this._timers.delete(timer);
                resolve(true);
            };
            modal.addEventListener(type, onEvent, { once: true });
        });
    }

    _onTarget(element) {
        if ((!this._target || this._target.isConnected === false) && this._lit && this._lit.state === this._state) {
            this._target = firstVisible(document, this._lit.selector) || this._target;
        }
        return Boolean(this._target && (this._target === element || this._target.contains(element)));
    }

    _reach(step) {
        this._stepAt = Date.now();
        reportProgress("walkthrough_step_reached", { step });
    }

    _complete(step) {
        reportProgress("walkthrough_step_completed", { step, ms: Math.max(0, Math.round(Date.now() - (this._stepAt ?? Date.now()))) });
        this._resumeIndex = this.stepsValue.indexOf(step) + 1;
    }

    _stepName() {
        return this.stepsValue[Math.min(this._index ?? 0, this.stepsValue.length - 1)];
    }

    /** Steps are named, never numbered: their order is WALKTHROUGH_STEPS's (stepsValue). */
    _setStep(step) {
        this._index = this.stepsValue.indexOf(step);
    }

    _onStep(step) {
        return this._phase === "step" && this.stepsValue[this._index] === step;
    }

    _onState(state) {
        return this._phase === "step" && this._state === state;
    }

    _onDashboard() {
        return new URL(this.dashboardValue, location.href).pathname === location.pathname;
    }

    _timing() {
        return onboardTiming(this.cardTarget);
    }

    /** The body mark, never before load + SETTLE_MS, and only while the dialog is open. */
    _mark(value) {
        const wait = this._loadedAt === null ? SETTLE_MS : this._loadedAt + SETTLE_MS - performance.now();
        if (wait > 0) {
            this._later(() => this._mark(value), wait);
            return;
        }
        if (this.element.hasAttribute("open")) document.body.setAttribute(BODY_MARK, value);
    }

    _settled() {
        return this._loadedAt !== null && performance.now() - this._loadedAt >= SETTLE_MS;
    }

    _noteLoad() {
        if (document.readyState === "complete") {
            this._loadedAt = performance.now();
            return;
        }
        this._loadedAt = null;
        this._loadListener = () => { this._loadedAt = performance.now(); };
        window.addEventListener("load", this._loadListener, { once: true });
    }

    /** What the current phase listens to, after a permanent-element reconnect. */
    _rearm() {
        if (this._phase === "armed" && this._armedForm && this._onFormData) {
            this._armedForm.removeEventListener("formdata", this._onFormData);
            this._armedForm.addEventListener("formdata", this._onFormData);
        }
        // The spotlight lives in this dialog too and picks its own target back up.
        if (this._onStep("ask_chip") || this._phase === "chat") this._watchChat();
    }

    _hideFinishedLines() {
        if (readMemo(this._storage(), this.userValue) !== "finished") return;
        document.querySelectorAll("[data-walkthrough-entry]").forEach((line) => { line.hidden = true; });
    }

    _storage() {
        try {
            return window.localStorage;
        } catch {
            return null;
        }
    }

    _later(fn, ms) {
        const timer = setTimeout(() => {
            this._timers.delete(timer);
            fn();
        }, ms);
        this._timers.add(timer);
        return timer;
    }

    _clearTimers() {
        this._timers.forEach((timer) => clearTimeout(timer));
        this._timers.clear();
    }
}
